import React, {FC, useCallback} from 'react';
import uuid from 'uuid-random';

import {
  ApplicationsPanelId,
  ViewerPanelId,
} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  ApplicationDraft,
  ApplicationSingleConversation,
  MessageComposerContext,
} from '../../../applications/bridge/v2/types/bridgeTypesV2';
import {
  ConversationActionContextFinder,
  ConversationInboxIdsFinder,
  ConversationNamespaceFinder,
} from '../../../helpers/models/conversationHelpers';
import {ApplicationEntryPointModel} from '../../../models/api/applicationEntryPointModel';
import {applicationListConversationCommentsAction} from '../../../store/data/applications/applicationCommentsActions';
import {
  applicationAssignConversationsAction,
  applicationMoveConversationsAction,
  applicationSetConversationsStatusAsyncAction,
  applicationTagConversationsAsyncAction,
  applicationUntagConversationsAsyncAction,
} from '../../../store/data/applications/applicationConversationsActions';
import {applicationListConversationRecipientsAction} from '../../../store/data/applications/applicationRecipientsActions';
import {applicationDownloadAttachmentForMessageAction} from '../../../store/data/applications/applicationsAttachmentActions';
import {
  applicationDeleteDraftAction,
  applicationListConversationsMessagesAction,
} from '../../../store/data/applications/applicationsMessagesActions';
import {
  addApplicationTopicToConversationsAsyncAction,
  removeApplicationLinkFromConversationAsyncAction,
} from '../../../store/data/applications/applicationsTopicsActions';
import {removePanelAction} from '../../../store/panels/panelsActions';
import {ContextBuilder} from './applicationContextBase';
import {ApplicationContextHostContainer} from './applicationContextHostContainer';

/*
 * Props.
 */

interface MessageComposerApplicationContextHostProps {
  applicationDraft: ApplicationDraft;
  entryPoint: ApplicationEntryPointModel;
  conversation: ApplicationSingleConversation | undefined;
  conversationId: number | undefined;
  findConversationActionContext: ConversationActionContextFinder;
  findConversationNamespace: ConversationNamespaceFinder;
  findConversationInboxIds: ConversationInboxIdsFinder;
  applicationsPanelId: ApplicationsPanelId;
  viewerPanelId: ViewerPanelId | undefined;
}

/*
 * Component.
 */

export const MessageComposerApplicationContextHost: FC<MessageComposerApplicationContextHostProps> = (
  props,
) => {
  /*
   * Build the context.
   */

  const {
    viewerPanelId,
    entryPoint,
    conversation,
    conversationId,
    applicationsPanelId,
    findConversationActionContext,
    findConversationNamespace,
    findConversationInboxIds,
    applicationDraft,
  } = props;
  const {applicationId} = entryPoint;
  const contextId = uuid();
  const contextBuilder: ContextBuilder = useCallback(
    (baseContext, dispatch) => {
      const messageComposerContext: MessageComposerContext = {
        ...baseContext,
        id: contextId,
        type: 'messageComposer',
        conversation,
        draft: applicationDraft,

        addLink: (externalUrl, name) => {
          if (!conversationId) {
            throw new Error("The draft's conversation is not selected.");
          }
          return dispatch(
            addApplicationTopicToConversationsAsyncAction(applicationId, [conversationId], {
              externalUrl,
              name,
            }),
          );
        },

        assign: async (teammateId) => {
          if (!conversationId) {
            throw new Error("The draft's conversation is not selected.");
          }
          return dispatch(
            applicationAssignConversationsAction([conversationId], teammateId, findConversationInboxIds),
          );
        },

        move: async (inboxId) => {
          if (!conversationId) {
            throw new Error("The draft's conversation is not selected.");
          }
          dispatch(
            applicationMoveConversationsAction(
              [conversationId],
              inboxId,
              findConversationNamespace,
              undefined,
            ),
          );
        },

        setStatus: async (status) => {
          if (!conversationId || !viewerPanelId) {
            throw new Error("The draft's conversation is not selected.");
          }
          dispatch(
            applicationSetConversationsStatusAsyncAction(
              viewerPanelId,
              [conversationId],
              status,
              findConversationActionContext,
            ),
          );
        },

        tag: async (tagIds) => {
          if (!conversationId) {
            throw new Error("The draft's conversation is not selected.");
          }
          await dispatch(applicationTagConversationsAsyncAction([conversationId], tagIds));
        },

        untag: async (tagIds) => {
          if (!conversationId) {
            throw new Error("The draft's conversation is not selected.");
          }
          await dispatch(applicationUntagConversationsAsyncAction([conversationId], tagIds));
        },

        removeLink: async (linkId) => {
          if (!conversationId) {
            throw new Error("The draft's conversation is not selected.");
          }
          await dispatch(
            removeApplicationLinkFromConversationAsyncAction(applicationId, linkId, [conversationId]),
          );
        },

        listMessages: async () => {
          if (!conversationId || !viewerPanelId) {
            throw new Error("The draft's conversation is not selected.");
          }
          return dispatch(applicationListConversationsMessagesAction(viewerPanelId, conversationId));
        },

        listComments: async () => {
          if (!conversationId || !viewerPanelId) {
            throw new Error("The draft's conversation is not selected.");
          }
          return dispatch(applicationListConversationCommentsAction(viewerPanelId, conversationId));
        },

        downloadComposerAttachment: async (attachmentId: string) =>
          dispatch(applicationDownloadAttachmentForMessageAction(applicationDraft.id, attachmentId)),

        listRecipients: async () => {
          if (!conversationId || !viewerPanelId) {
            throw new Error("The draft's conversation is not selected.");
          }
          return dispatch(applicationListConversationRecipientsAction(viewerPanelId, conversationId));
        },

        close: async () => {
          dispatch(removePanelAction(applicationsPanelId));
        },

        updateDraft: async (draftId, update) => baseContext.updateDraft(applicationDraft.id, update),

        closeDraft: async () => {
          dispatch(removePanelAction(applicationsPanelId));
          dispatch(applicationDeleteDraftAction(applicationDraft.id));
        },
      };

      return messageComposerContext;
    },
    [
      conversation,
      conversationId,
      applicationId,
      viewerPanelId,
      applicationsPanelId,
      applicationDraft,
      findConversationActionContext,
      findConversationNamespace,
    ],
  );

  /*
   * Use the base context host with our extra context props.
   */

  return (
    <ApplicationContextHostContainer
      panelId={viewerPanelId ?? applicationsPanelId}
      entryPoint={entryPoint}
      contextBuilder={contextBuilder}
    />
  );
};
